import React from 'react'
import PropTypes from 'prop-types'
import NextLink from 'next/link'
import { isLocalUrl, getUrlObject } from 'tools/routes'
import Router from 'next/router'
import withRoute from 'decorators/Route'
import { DEFAULT_LOCALE } from 'env'

const removeBaseUrl = (href) => {
  const urlObject = getUrlObject()
  const protocol = urlObject.protocol
  const host = urlObject.host
  const baseUrl = `${protocol}//${host}`
  const _href = (href || '/').split(baseUrl)
  return _href[_href.length - 1]
}

const parseStringHref = (href = '', preservedQuery = {}, locale = '') => {
  if (isLocalUrl(href)) {
    href = removeBaseUrl(href)
  }
  const [ pathnameAndQuery, hash ] = href.split('#')
  const [ pathname, _query = '' ] = pathnameAndQuery.split('?')
  const query = _query.split('&').filter(e => e !== '').reduce((acc, cur) => {
    const [ key, value ] = cur.split('=')
    acc[key] = value
    return acc
  }, {})
  return {
    pathname: '/',
    query: {
      ...preservedQuery,
      ...query,
      path: pathname.split('/').filter(path => ![locale, ''].includes(path))
    },
    hash
  }
}

const parseObjectHref = ({ pathname, query, ...rest }, preservedQuery = {}) => {
  return {
    pathname: '/',
    query: {
      ...preservedQuery,
      ...query,
      path: pathname.split('/').filter(e => e !== '')
    },
    ...rest
  }
}

const getHref = ({ href = '', route }) => {
  const prevQuery = Link.preservedQuery.reduce((acc, cur) => {
    acc[cur] = route[cur]
    return acc
  }, {})
  return typeof href === 'string' ? parseStringHref(href, prevQuery, route.locale) : parseObjectHref(href, prevQuery)
}

const getAsHref = ({ href, as, withoutLocale, route }) => {
  if (as) return as
  const locale = withoutLocale ? '' : (href.query.locale ? `/${href.query.locale}` : `/${DEFAULT_LOCALE}`)
  const pathname = href.query.path.reduce((acc, cur) => acc.concat(`/${cur}`), '')
  const query = Object.keys(href.query).reduce((acc, key) => {
    if (Link.preservedQuery.includes(key)) return acc
    return `${acc}${key}=${href.query[key]}&`
  }, '?')
  const hash = href.hash ? `#${href.hash}` : ''
  return `${locale}${route.previewMode === 'true' ? `/preview/${route.previewToken}` : ''}${pathname}${query === '?' ? '' : query}${hash}`
}

class Link extends React.PureComponent {
  static propTypes = {
    ...NextLink.propTypes,
    withoutLocale: PropTypes.bool
  }

  static defaultProps = {
    withoutLocale: false
  }

  static preservedQuery = [
    'locale',
    'previewMode',
    'previewToken',
    'path'
  ]

  onClick = (e) => {
    const { prefetch, replace, shallow, passHref, scroll, onClick, ...props } = this.props
    if (typeof onClick === 'function') {
      onClick(e)
    }

    // The following 'if' block is adapted from https://github.com/zeit/next.js/blob/9609fe453d8ce3e67ab89733f52643fc85c16b72/packages/next/client/link.tsx#L140
    const { nodeName, target } = e.currentTarget
    if (
      nodeName === 'A' &&
      ((target && target !== '_self') ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        (e.nativeEvent && e.nativeEvent.which === 2))
    ) {
      return
    }

    if (!isLocalUrl(props.href)) {
      return
    }

    if (!e.defaultPrevented) {
      e.preventDefault()
      const href = getHref(props)
      const asHref = getAsHref({ ...props, href })
      const method = replace ? 'replace' : 'push'
      Router[method](href, asHref, { shallow, scroll })
    }
  }

  render () {
    const { prefetch, replace, shallow, passHref, scroll, router, route, withoutLocale, ...props } = this.props
    return <a {...props} onClick={this.onClick} style={{ outline: 'none' }} />
  }
}

export default withRoute(Link)
